import React, { useContext, useEffect, useState } from 'react';
import { Tooltip, TooltipProvider, TooltipTrigger } from '../components/ui/Tooltip';
import { TooltipContent } from '@radix-ui/react-tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Toggle } from '../components/ui/Toggle';
import { APIContext } from '../contexts/APIContext';
import { ChatbotContext } from '../contexts/ChatbotContext';
import { SessionContext } from '../contexts/SessionContext';
import { PAGE_VIEW } from '../types';
import { trackUserClicks, getValidToken } from '../utils';
import FeedbackModal from './FeedbackModal';
import DOMPurify from 'dompurify';

const Leads = [
  { name: 'David Harrison', avatar: '/icons/DavidHarrison.svg' },
  { name: 'Ulf Grosskopf', avatar: '/icons/UlfGrosskopf.svg' },
];

const teams = [
  { name: 'Stephani Calderon', avatar: '/icons/StephaniCalderon.svg' },
  { name: 'Ksenia Alexeeva​', avatar: '/icons/KseniaAlexeeva.svg' },
  { name: 'Jason DePreaux​', avatar: '/icons/JasonDePreaux.svg' },
  { name: 'Natalie Barr', avatar: '/icons/NatalieBar.svg' },
];

const FAQ = [
  {
    num: 1,
    question: 'What is Generative Market Analysis?',
    Ans: 'Generative Market Analysis (GMA) uses GenAI technology to rapidly analyze proprietary data from the Corporate Strategy & Growth (CS&G) team and provide unique market and industry insights. Through a chat-GPT-style interface, GMA helps CS&G practitioners quickly get up to speed on markets and products while enabling faster, more precise recommendations for clients.',
  },
  {
    num: 2,
    question: 'Who do I reach out to if I cannot access GMA?',
    Ans: 'If you are not aligned to CS&G, you will not have automatic access to GMA. If you are a friend of the practice and see partnership opportunities using GMA, please reach out to <a href="mailto:GMA.AccessSupport@accenture.com?subject=GMA%20Application%20Access%20Request&body=Hello%20team,%0D%0A%0D%0AI%20would%20like%20to%20request%20access%20to%20GMA,%20please%20find%20the%20requirement%20details%20below.%20I%20understand%20that%20if%20I%20do%20not%20complete%20the%20form%20below,%20my%20request%20will%20not%20be%20processed.%0D%0A%0D%0AGenerative%20Market%20Analysis%20has%20limited%20access%20to%20our%20Corporate%20Strategy%20%26%20Growth%20(CS%26G)%20practice.%0D%0AHave%20you%20been/%20will%20be%20open%20to%20working%20with%20someone%20from%20the%20CS%26G/%20growth%20strategy%20group%20for%20this%20proposal/project?%20(Y/N)%0D%0AIs%20this%20a%20growth%20strategy%20or%20market%20research%20opportunity?%20(Y/N)%0D%0AProject%20scope:%20please%20describe%0D%0AEIDs%20for%20access:%20please%20provide%0D%0AProject%20Lead%20EID:%0D%0AClient%20Name:%20please%20provide%0DIs%20this%20a%20proposal%20or%20project%20delivery?%0D%0AGeography/country:%20please%20provide%0D%0AMarket%20Unit:%20please%20provide%0D%0AHow%20long%20do%20you%20need%20to%20use%20GMA?%0D%0A%0D%0AThank%20you!" class="text-purple-500 underline">GMA.AccessSupport</a>',
  },
  {
    num: 3,
    question: 'What data sources is GMA using?',
    Ans: 'GMA’s value proposition to practitioners is our CS&G CORE database, which contains thousands of primary research transcripts from market experts across the globe. CORE is rich with industry-specific data, including health, medical devices, life sciences, aerospace & defense, and high tech. With GMA, you also have access to three other data source pools: Azure OpenAI, Accenture internal data, and trusted third-party data sources.',
  },
  {
    num: 4,
    question: 'How do I know which data source GMA is using?',
    Ans: 'Each response will have a description at the bottom starting with “Source:” that indicates one of three data buckets being used: CORE, Azure OpenAI, or Gen AI on the Console. If primary transcripts are visible under the “Response Details” tab, the response is using data from CORE only.',
  },
  {
    num: 5,
    question: 'How do I export my chat history?',
    Ans: 'You have two options to export responses. The first option is through our Storybuilder capability that allows you to quickly generate PowerPoint decks for your teams. Remember to click the “+” button under your response in order to see the result show up under the “Storybuilder” tab on the right side of the chat page. Once you are in the “Storybuilder” tab, you can later remove responses you do not want included in your final export. Remember to select the “Generate PowerPoint” button at the bottom to get a downloaded copy of your responses. <br /> The other option to export your responses through docx or pptx format is through our “Export Chat” icon on the right side of the chat page that shows a downward arrow. Unlike the Storybuilder capability, this export feature allows you to download the full chat history in either docx or pptx format.',
  },
  {
    num: 6,
    question: 'How can I access the user guide?',
    Ans: 'The GMA user guide is available <a href="https://ts.accenture.com/:p:/s/growth.ai310/ER20i3KaWGhLtl_4tOvB1PcBJVVrQOO5g0fNN7LQwBh1PA?e=kdsWjd" class="text-purple-500 underline" target="_blank">here</a> (you may have to access if you are not aligned to CS&G). There are also links to the user guide on the <a href="https://reinventionconsole.accenture.com/digitalbriefcase/new/briefcase/4f5e6cda-5acd-4deb-84bc-6c09371d6f2e/preview-briefcase?mode=reader" class="text-purple-500 underline" target="_blank">CS&G Solution Tablet</a> and in the <a href="https://teams.microsoft.com/l/team/19%3AppOIpJ-s-vDMDQoMpr6KqJUylmlx2EtSqJy3cGnaWHI1%40thread.tacv2/conversations?groupId=ba2dbc0f-42ad-4575-8f60-4e3619003022&tenantId=e0793d39-0939-496d-b129-198edd916feb" class="text-purple-500 underline" target="_blank">Microsoft Teams Growth.AI User Forum</a>.',
  },
  {
    num: 7,
    question: 'Where can I report feedback?',
    Ans: 'We highly encourage everyone to tell us about any tool or customer experience areas of improvement as well as bugs so we can continue to iterate and improve on our product!',
  },
  {
    num: 8,
    question: 'What Growth.AI activities are happening from an enablement perspective?',
    Ans: 'There are several ongoing initiatives in CS&G to empower our practitioners with the skills and context needed to successfully leverage our Growth.AI tools. Some enablement & change activities include: <br /> •	Monthly Lunch & Learn Sessions. Sessions with our CS&G leadership to showcase upcoming features and enhancements, short Gen AI trainings, case studies, Q&A, and more! <br />•	Growth.AI Champion Network. Smaller, focus group-style info sessions for direct, personalized feedback. <br />•	Growth.AI Trainings. Bite-size demos held each month with smaller groups to learn about the latest features and updates in Growth.AI.',
  },
  {
    num: 9,
    question: 'How can I get involved?',
    Ans: 'If you want to get involved with Growth.AI in a plus 1 capacity to help build out our tools, document and socialize efforts to showcase the value being delivered to clients, or create materials to support and enable our practitioners, reach out to <a href="mailto:GMA.AccessSupport@accenture.com?subject=GMA%20Application%20Access%20Request&body=Hello%20team,%0D%0A%0D%0AI%20would%20like%20to%20request%20access%20to%20GMA,%20please%20find%20the%20requirement%20details%20below.%20I%20understand%20that%20if%20I%20do%20not%20complete%20the%20form%20below,%20my%20request%20will%20not%20be%20processed.%0D%0A%0D%0AGenerative%20Market%20Analysis%20has%20limited%20access%20to%20our%20Corporate%20Strategy%20%26%20Growth%20(CS%26G)%20practice.%0D%0AHave%20you%20been/%20will%20be%20open%20to%20working%20with%20someone%20from%20the%20CS%26G/%20growth%20strategy%20group%20for%20this%20proposal/project?%20(Y/N)%0D%0AIs%20this%20a%20growth%20strategy%20or%20market%20research%20opportunity?%20(Y/N)%0D%0AProject%20scope:%20please%20describe%0D%0AEIDs%20for%20access:%20please%20provide%0D%0AProject%20Lead%20EID:%0D%0AClient%20Name:%20please%20provide%0DIs%20this%20a%20proposal%20or%20project%20delivery?%0D%0AGeography/country:%20please%20provide%0D%0AMarket%20Unit:%20please%20provide%0D%0AHow%20long%20do%20you%20need%20to%20use%20GMA?%0D%0A%0D%0AThank%20you!" class="text-purple-500 underline">GMA.AccessSupport</a>',
  },
  {
    num: 10,
    question: 'How can I understand the features that are being released each month?',
    Ans: 'There are several places where new features will be announced: monthly CS&G practice connect calls, Lunch & Learn sessions, email announcements at the beginning of each month, and posts on the <a href="https://teams.microsoft.com/l/team/19%3AppOIpJ-s-vDMDQoMpr6KqJUylmlx2EtSqJy3cGnaWHI1%40thread.tacv2/conversations?groupId=ba2dbc0f-42ad-4575-8f60-4e3619003022&tenantId=e0793d39-0939-496d-b129-198edd916feb" class="text-purple-500 underline" target="_blank">Microsoft Teams Growth.AI User Forum</a>.',
  },
  {
    num: 11,
    question: 'When will I see the next release of features for GMA?',
    Ans: 'Each month our development team (AEH) works on the next set of features gathered from our various Growth.AI sessions from users. We push these features into production and you will see them at the beginning of each month.',
  },
  {
    num: 12,
    question: 'I am working on a proposal for a client and I want to highlight GMA. What content can I use with them?',
    Ans: 'We are currently building Growth.AI Stage 0 materials for client-facing engagements and expecting them to be ready by end of May. However, please contact <a href="mailto:GMA.AccessSupport@accenture.com?subject=GMA%20Application%20Access%20Request&body=Hello%20team,%0D%0A%0D%0AI%20would%20like%20to%20request%20access%20to%20GMA,%20please%20find%20the%20requirement%20details%20below.%20I%20understand%20that%20if%20I%20do%20not%20complete%20the%20form%20below,%20my%20request%20will%20not%20be%20processed.%0D%0A%0D%0AGenerative%20Market%20Analysis%20has%20limited%20access%20to%20our%20Corporate%20Strategy%20%26%20Growth%20(CS%26G)%20practice.%0D%0AHave%20you%20been/%20will%20be%20open%20to%20working%20with%20someone%20from%20the%20CS%26G/%20growth%20strategy%20group%20for%20this%20proposal/project?%20(Y/N)%0D%0AIs%20this%20a%20growth%20strategy%20or%20market%20research%20opportunity?%20(Y/N)%0D%0AProject%20scope:%20please%20describe%0D%0AEIDs%20for%20access:%20please%20provide%0D%0AProject%20Lead%20EID:%0D%0AClient%20Name:%20please%20provide%0DIs%20this%20a%20proposal%20or%20project%20delivery?%0D%0AGeography/country:%20please%20provide%0D%0AMarket%20Unit:%20please%20provide%0D%0AHow%20long%20do%20you%20need%20to%20use%20GMA?%0D%0A%0D%0AThank%20you!" class="text-purple-500 underline">GMA.AccessSupport</a> for assistance to gain access to the working document that your team can use today.',
  },
  {
    num: 13,
    question: 'I do not see my industry covered in GMA. What can I do to help?',
    Ans: 'In order to expand our depth and breadth of industry coverage in GMA, we need to continuously ingest additional primary research into CORE – this is often known as “feed the beast”. If your industry is not covered, we need your help to ingest these transcripts into CORE! We encourage you to visit our Data Augmentation training courses to learn more about how to prepare your transcripts and conduct primary research.',
  },
];

const FeedbackComponent = () => {
  const {
    websocketConnected,
    chatSessionId,
    userDetails,
    websocketConnectionRef: { current: websocket },
    setRequestNumber,
    WBUId,
    AppName,
    accounts,
  } = useContext(APIContext);
  const { pageView, setPageView, isPromptGenerating } = useContext(SessionContext);
  const [Request, setRequest] = useState<boolean>(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [aLLFAQS, setALLFAQS] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const filteredQuestions = FAQ.filter((item) => item.question.toLowerCase().includes(searchTerm.toLowerCase()));
  const [file, setFile] = useState<File | null>(null);
  const displayQuestions = aLLFAQS ? filteredQuestions : filteredQuestions.slice(0, 5);

  const handleNavbarClick = async (view: PAGE_VIEW) => {
    const token = await getValidToken();
    trackUserClicks(websocket, 'GMA Landing Page', userDetails, chatSessionId, WBUId, AppName, accounts, token);
    if (view === pageView) {
      setPageView(PAGE_VIEW.LANDING);
    } else {
      setPageView(view);
    }
  };

  const toggleFAQ = (num: number) => {
    setOpenFAQ(openFAQ === num ? null : num);
  };

  const handleRaiseARequest = () => {
    setRequest(true);
    setRequestNumber('');
    setFile(null);
  };

  useEffect(() => {
    //reset the state to default values on component unmount
    return () => {
      setALLFAQS(false);
    };
  }, []);

  return (
    <div className="relative h-full w-full bg-feeback-bg-img bg-cover bg-white overflow-y-auto">
      <div className="feedback-container absolute z-9 px-6 w-full">
        <div className="flex justify-between items-center mt-[5px] mr-4">
          <div className="flex items-start space-x-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <img
                    src="/icons/HomeIcon.svg"
                    className={`w-4 h-4 cursor-pointer mt-3 ${isPromptGenerating && 'not-clickable cursor-default'}`}
                    onClick={() => handleNavbarClick(PAGE_VIEW.LANDING)}
                  />
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="bg-[#505050] border-0 max-w-[218px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2"
                  sideOffset={5}
                >
                  <p className="tooltip-content text-white text-xs capitalize not-italic">Home</p>
                  <TooltipPrimitive.Arrow width={11} height={5} style={{ fill: '#505050' }} />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <p
              className="font-graphik text-base text-black font-medium leading-normal mx-4 p-2 text-center"
              style={{ marginLeft: '10px' }}
            >
              GMA
            </p>
          </div>
          <div className="relative">
            <img
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4"
              alt="Search"
              src="icons/Search.svg"
            />
            <input
              type="text"
              placeholder="Search..."
              className="w-full pl-10 pr-10 py-2 text-sm focus:outline-none shadow-md"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <img
              src="icons/Close.svg"
              alt="Clear"
              className=" absolute right-4 top-1/2 transform -translate-y-1/2 bg-transparent  w-4 h-4 cursor-pointer"
              onClick={() => setSearchTerm('')}
            />
          </div>
        </div>
        <div className="w-[86%] min-h-[800px] m-20 shadow-lg bg-white rounded-md border border-[#F5E5FF]">
          <div className="Feedback-FAQ p-6">
            <div className={`flex  ${aLLFAQS ? '' : 'justify-between'}`}>
              {aLLFAQS && (
                <img
                  className="cursor-pointer"
                  alt="backarrow"
                  src="/icons/backarrow.svg"
                  onClick={() => setALLFAQS(false)}
                ></img>
              )}
              <label className="font-semibold text-xl ml-4">
                {displayQuestions.length > 0 ? (aLLFAQS ? 'ALL FAQs' : 'Frequently Asked Questions') : ''}
              </label>
              <a className="text-[#A100FF] cursor-pointer" onClick={() => setALLFAQS(true)}>
                {displayQuestions.length > 0 ? !aLLFAQS && 'ALL FAQS' : ''}
              </a>
            </div>
            <div className="p-6">
              {displayQuestions.length > 0 ? (
                displayQuestions.map((item) => (
                  <div className={`border-y ${openFAQ === item.num ? 'border-[#A100FF]' : ''} p-4`} key={item.num}>
                    <div className={`flex items-start justify-between ${openFAQ === item.num ? 'font-semibold' : ''}`}>
                      <div>
                        {item.num}. &nbsp;
                        {item.question}
                      </div>
                      <img
                        onClick={() => toggleFAQ(item.num)}
                        className="align-middle cursor-pointer"
                        src={openFAQ === item.num ? '/icons/activeOpen.svg' : '/icons/Down.svg'}
                        alt="expand"
                      />
                    </div>
                    {openFAQ === item.num && (
                      <div
                        className="text-start mt-2"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.Ans) }}
                      />
                    )}
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center text-center">
                  <p className="w-[28rem]">
                    Sorry, no results found for &#39;{searchTerm}&#39;. Try entering fewer or more general search terms.
                  </p>
                </div>
              )}
            </div>
          </div>
          {!aLLFAQS && (
            <>
              <div className="Feedback-Request bg-[#F9F9F9] min-h-[130px] p-8 flex justify-between items-center">
                <div className="flex flex-col items-start">
                  <span className="text-2xl">Didn’t find what you were looking for?</span>
                  <span className="text-[#707070]">
                    Explore all FAQ’s for more guided questions or Raise a request with us.
                  </span>
                </div>
                <button className="bg-black text-white py-2 px-4 hover:bg-[#460073]" onClick={handleRaiseARequest}>
                  Raise a request
                </button>
                {Request && <FeedbackModal setIsModalOpen={setRequest} file={file} setFile={setFile} />}
              </div>
              <div className="feedback-contact w-[100%] p-12">
                <div className="flex justify-between">
                  <p className="text-2xl font-semibold">Contact</p>
                </div>
                <p className="flex items-start text-base py-6">Global Growth.AI Leads</p>
                <div className="flex gap-20">
                  {Leads.map((contact, index) => (
                    <div className="flex flex-col items-center mb-8 gap-3" key={index}>
                      <div className="relative flex justify-center h-20 w-20 items-center rounded-full text-white z-10">
                        <img src={contact.avatar} className="h-20 w-20 object-cover rounded-full z-9" alt="Avatar" />
                      </div>
                      <div>
                        <div>
                          <p className="font-medium text-base text-center">{contact.name}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="flex items-start text-base py-6"> Our Core Team for Day-to-Day Foresight.AI Needs</p>
                <div className="flex gap-20">
                  {teams.map((contact, index) => (
                    <div className="flex flex-col items-center mb-8 w-24 gap-3" key={index}>
                      <div className="relative flex justify-center h-20 w-20 items-center rounded-full text-white z-10">
                        <img
                          src={contact.avatar}
                          alt={contact.name}
                          className="h-20 w-20 object-cover rounded-full z-9"
                        />
                      </div>
                      <div className="justify-center">
                        <div>
                          <p className="font-medium text-base text-center">{contact.name}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackComponent;
